import { useSelector } from "react-redux";

/**
 * React equivalent of the ASP `ClientGetClientParam(context, name, default)` helper.
 * Reads from the globally-fetched client params list (`fetchClientParams()`,
 * dispatched once in App.jsx into `state.clientParams.list`).
 *
 * NOTE: despite the field names, the stored rows put the ASP "context"
 * (e.g. "Bio", "Warrants") in `paramName` and the ASP "name"
 * (e.g. "MultipleAliases") in `paramContext` — confirmed against the
 * Global Client Parameters grid, and consistent with App.jsx's own
 * `param.paramContext === "sessionTimeOut" && param.paramName === "Globals"`
 * check. This helper's own (context, name) argument order still matches
 * ASP's `ClientGetClientParam(context, name, default)` — only the
 * underlying field lookup is swapped to match the actual data shape.
 *
 * @param {string} context      - e.g. "Bio", "Warrants", "Staff", "CivilPapers"
 * @param {string} name         - e.g. "MultipleAliases", "IncludeBioExtra"
 * @param {string} [defaultValue] - returned when no matching param row exists
 * @returns {string}
 */
export const useClientParam = (context, name, defaultValue = "") => {
  return useSelector((state) => {
    const list = state?.clientParams?.list;
    if (!list?.length) return defaultValue;
    const match = list.find(
      (p) =>
        p?.paramName?.toLowerCase() === context?.toLowerCase() &&
        p?.paramContext?.toLowerCase() === name?.toLowerCase()
    );
    return match?.paramValue ?? defaultValue;
  });
};

/**
 * Boolean convenience wrapper — mirrors ASP's `CBool(ClientGetClientParam(...))`
 * pattern used throughout BioInterface.asp (values are stored as "True"/"False" strings).
 *
 * @param {string} context
 * @param {string} name
 * @param {boolean} [defaultValue=false]
 * @returns {boolean}
 */
export const useClientParamBool = (context, name, defaultValue = false) => {
  const raw = useClientParam(context, name, defaultValue ? "True" : "False");
  return String(raw).trim().toLowerCase() === "true";
};
